import { useCallback, useMemo } from "react";

import {
  AssignUserSelector,
  NOT_ASSIGNED,
  useAssignUserSingleSelectHelper,
} from "@/components/assign-user-selector";
import { useTrackAnalytics } from "@/hooks/analytics.hooks";
import { useAssignUserToLocationMutation } from "@/hooks/queries/screener-locations.queries";
import type { ScreenerLocation } from "@/types/screener-location";
import type { PropsWithTableInfo } from "@/types/third-party";
import type { UserListUser } from "@/types/user";
import type { Nullable } from "@/types/utils";
import { TestID } from "@/utils/helpers";

export const AssignUserCell = ({
  info,
}: PropsWithTableInfo<ScreenerLocation, Nullable<UserListUser>>) => {
  const track = useTrackAnalytics();
  const assignedUser = info.getValue();
  const { onAssignedToMeView } = useAssignUserSingleSelectHelper();
  const { mutate: assignUser, isPending: mutationPending } =
    useAssignUserToLocationMutation();

  const locationId = useMemo(
    () => info.row.original.id,
    [info.row.original.id],
  );

  const handleChange = useCallback(
    (newValue: Nullable<string>) => {
      const userId = newValue === NOT_ASSIGNED ? null : newValue;

      // Avoid sending calls if the selected value is already selected.
      if (
        (assignedUser && assignedUser.id === userId) ||
        (!assignedUser && !userId)
      ) {
        return;
      }

      track("Assign Recruiter", {
        location_id: locationId,
        user_id: userId,
      });

      assignUser({
        locationId,
        userId,
      });
    },
    [assignUser, assignedUser, locationId, track],
  );

  return (
    <AssignUserSelector
      baseTestId={
        new TestID(
          onAssignedToMeView ? "my-locations" : "screener-locations",
          "assigned-to-cell",
          undefined,
          info.row.index,
        )
      }
      keyPrefix={`assign_user_cell_${info.row.original.id}`}
      loading={mutationPending}
      onChange={handleChange}
      value={assignedUser ? [assignedUser.id] : []}
    />
  );
};
